// =============================================================================
// Module Project - Hook: useTaskDependenciesStoreV2
// Zustand store for V2 task dependencies (FS model)
// =============================================================================

import { create } from 'zustand';
import {
  fetchProjectDependenciesV2,
  fetchBlockingTasks,
  fetchBlockedTasks,
  createDependencyV2,
  deleteDependencyV2,
  searchProjectTasksV2,
  fetchTasksDependencyInfoV2,
  getBlockingReasons,
  type TaskDependencyV2,
  type TaskDependencyInfoV2,
  type DependencyTypeV2,
} from '../data/taskDependencyRepositoryV2';
import { createActivityLog } from '../data/activityRepository';

// ─────────────────────────────────────────────────────────────────────────────
// STATE INTERFACE
// ─────────────────────────────────────────────────────────────────────────────

export interface TaskDependenciesStateV2 {
  // Project-level dependencies
  dependencies: TaskDependencyV2[];
  loading: boolean;
  error: string | null;
  
  // Task-specific data (for drawer)
  currentTaskId: string | null;
  predecessors: Array<{ id: string; title: string; status_code: string; dependencyId: string }>;
  successors: Array<{ id: string; title: string; status_code: string; dependencyId: string }>;
  isBlocked: boolean;
  blockingReasons: Array<{ id: string; title: string; status_code: string }>;
  loadingTask: boolean;
  
  // Batch info for views (Kanban, List, etc.)
  taskInfoMap: Map<string, TaskDependencyInfoV2>;

  // Actions
  loadProjectDependencies: (projectId: string) => Promise<void>;
  loadTaskDependencies: (taskId: string, terminalStatuses?: string[]) => Promise<void>;
  loadTasksInfo: (taskIds: string[], terminalStatuses?: string[]) => Promise<void>;
  
  addDependency: (
    orgId: string,
    projectId: string,
    predecessorTaskId: string,
    successorTaskId: string,
    userId: string,
    type?: DependencyTypeV2
  ) => Promise<{ success: boolean; error?: string }>;
  
  removeDependency: (
    dependencyId: string,
    taskId: string,
    userId: string,
    orgId: string
  ) => Promise<boolean>;
  
  searchTasks: (
    projectId: string,
    excludeTaskId: string,
    searchTerm?: string
  ) => Promise<Array<{ id: string; title: string; status_code: string }>>;
  
  // Selectors
  getPredecessors: (taskId: string) => TaskDependencyV2[];
  getSuccessors: (taskId: string) => TaskDependencyV2[];
  isTaskBlocked: (taskId: string) => boolean;
  getTaskBlockingReasons: (taskId: string) => Array<{ id: string; title: string; status_code: string }>;
  
  // Cleanup
  clearTask: () => void;
  clearAll: () => void;
}

// Terminal status codes (configurable)
const DEFAULT_TERMINAL_STATUSES = ['done', 'completed', 'closed', 'cancelled', 'archived'];

// ─────────────────────────────────────────────────────────────────────────────
// STORE
// ─────────────────────────────────────────────────────────────────────────────

export const useTaskDependenciesStoreV2 = create<TaskDependenciesStateV2>((set, get) => ({
  // Initial state
  dependencies: [],
  loading: false,
  error: null,
  currentTaskId: null,
  predecessors: [],
  successors: [],
  isBlocked: false,
  blockingReasons: [],
  loadingTask: false,
  taskInfoMap: new Map(),

  // ─────────────────────────────────────────────────────────────────────────
  // LOAD PROJECT DEPENDENCIES
  // ─────────────────────────────────────────────────────────────────────────
  loadProjectDependencies: async (projectId: string) => {
    set({ loading: true, error: null });
    
    try {
      const deps = await fetchProjectDependenciesV2(projectId);
      set({ dependencies: deps, loading: false });
    } catch (err) {
      console.warn('Loading project dependencies failed (non-blocking):', err);
      set({ error: 'Erreur lors du chargement des dépendances', loading: false });
    }
  },

  // ─────────────────────────────────────────────────────────────────────────
  // LOAD TASK DEPENDENCIES (for drawer)
  // ─────────────────────────────────────────────────────────────────────────
  loadTaskDependencies: async (taskId: string, terminalStatuses = DEFAULT_TERMINAL_STATUSES) => {
    set({ loadingTask: true, currentTaskId: taskId });
    
    try {
      const [preds, succs, reasons] = await Promise.all([
        fetchBlockingTasks(taskId),
        fetchBlockedTasks(taskId),
        getBlockingReasons(taskId, terminalStatuses),
      ]);
      
      const isBlocked = reasons.length > 0;
      
      set({
        predecessors: preds,
        successors: succs,
        isBlocked,
        blockingReasons: reasons,
        loadingTask: false,
      });
    } catch (err) {
      console.warn('Loading task dependencies failed (non-blocking):', err);
      set({ loadingTask: false });
    }
  },

  // ─────────────────────────────────────────────────────────────────────────
  // LOAD TASKS INFO (batch for views)
  // ─────────────────────────────────────────────────────────────────────────
  loadTasksInfo: async (taskIds: string[], terminalStatuses = DEFAULT_TERMINAL_STATUSES) => {
    if (taskIds.length === 0) {
      set({ taskInfoMap: new Map() });
      return;
    }
    
    try {
      const infoMap = await fetchTasksDependencyInfoV2(taskIds, terminalStatuses);
      set({ taskInfoMap: infoMap });
    } catch (err) {
      console.error('Error loading tasks info:', err);
    }
  },

  // ─────────────────────────────────────────────────────────────────────────
  // ADD DEPENDENCY
  // ─────────────────────────────────────────────────────────────────────────
  addDependency: async (
    orgId: string,
    projectId: string,
    predecessorTaskId: string,
    successorTaskId: string,
    userId: string,
    type: DependencyTypeV2 = 'FS'
  ) => {
    const result = await createDependencyV2({
      orgId,
      projectId,
      predecessorTaskId,
      successorTaskId,
      type,
      createdBy: userId,
    });

    if (result.success && result.dependency) {
      // Update project dependencies
      set(state => ({
        dependencies: [...state.dependencies, result.dependency!],
      }));

      // Log activity for successor task (the one that now has a dependency)
      await createActivityLog(
        orgId,
        successorTaskId,
        userId,
        'dependency_added',
        'dependency',
        null,
        `Bloquée par: ${result.dependency.predecessor_task?.title || predecessorTaskId}`
      );

      // Reload task dependencies if viewing this task
      const { currentTaskId } = get();
      if (currentTaskId === successorTaskId || currentTaskId === predecessorTaskId) {
        await get().loadTaskDependencies(currentTaskId);
      }
    }

    return { success: result.success, error: result.error };
  },

  // ─────────────────────────────────────────────────────────────────────────
  // REMOVE DEPENDENCY
  // ─────────────────────────────────────────────────────────────────────────
  removeDependency: async (
    dependencyId: string,
    taskId: string,
    userId: string,
    orgId: string
  ) => {
    const { dependencies, currentTaskId } = get();
    
    // Find the dependency to log info
    const dep = dependencies.find(d => d.id === dependencyId);
    
    const success = await deleteDependencyV2(dependencyId);

    if (success) {
      // Update project dependencies
      set(state => ({
        dependencies: state.dependencies.filter(d => d.id !== dependencyId),
      }));

      // Log activity
      if (dep) {
        await createActivityLog(
          orgId,
          dep.successor_task_id,
          userId,
          'dependency_removed',
          'dependency',
          dep.predecessor_task?.title || dep.predecessor_task_id,
          null
        );
      }

      // Reload task dependencies if viewing this task
      if (currentTaskId === taskId) {
        await get().loadTaskDependencies(currentTaskId);
      }
    }

    return success;
  },

  // ─────────────────────────────────────────────────────────────────────────
  // SEARCH TASKS
  // ─────────────────────────────────────────────────────────────────────────
  searchTasks: async (projectId, excludeTaskId, searchTerm) => {
    return searchProjectTasksV2(projectId, excludeTaskId, searchTerm);
  },

  // ─────────────────────────────────────────────────────────────────────────
  // SELECTORS
  // ─────────────────────────────────────────────────────────────────────────
  getPredecessors: (taskId: string) => {
    const { dependencies } = get();
    return dependencies.filter(d => d.successor_task_id === taskId);
  },

  getSuccessors: (taskId: string) => {
    const { dependencies } = get();
    return dependencies.filter(d => d.predecessor_task_id === taskId);
  },

  isTaskBlocked: (taskId: string) => {
    const { taskInfoMap, currentTaskId, isBlocked } = get();
    
    // If it's the current task in drawer, use detailed info
    if (currentTaskId === taskId) {
      return isBlocked;
    }
    
    // Otherwise use batch info
    const info = taskInfoMap.get(taskId);
    return info?.isBlocked ?? false;
  },

  getTaskBlockingReasons: (taskId: string) => {
    const { taskInfoMap, currentTaskId, blockingReasons } = get();
    
    // If it's the current task in drawer, use detailed info
    if (currentTaskId === taskId) {
      return blockingReasons;
    }
    
    // Otherwise use batch info
    const info = taskInfoMap.get(taskId);
    return info?.blockingTasks ?? [];
  },

  // ─────────────────────────────────────────────────────────────────────────
  // CLEANUP
  // ─────────────────────────────────────────────────────────────────────────
  clearTask: () => {
    set({
      currentTaskId: null,
      predecessors: [],
      successors: [],
      isBlocked: false,
      blockingReasons: [],
      loadingTask: false,
    });
  },

  clearAll: () => {
    set({
      dependencies: [],
      loading: false,
      error: null,
      currentTaskId: null,
      predecessors: [],
      successors: [],
      isBlocked: false,
      blockingReasons: [],
      loadingTask: false,
      taskInfoMap: new Map(),
    });
  },
}));

// ─────────────────────────────────────────────────────────────────────────────
// SELECTOR HOOKS (for optimized re-renders)
// ─────────────────────────────────────────────────────────────────────────────

export const useDependencies = () => 
  useTaskDependenciesStoreV2(state => state.dependencies);

export const useDependenciesLoading = () => 
  useTaskDependenciesStoreV2(state => state.loading);

export const useTaskPredecessors = () => 
  useTaskDependenciesStoreV2(state => state.predecessors);

export const useTaskSuccessors = () => 
  useTaskDependenciesStoreV2(state => state.successors);

export const useTaskIsBlocked = () => 
  useTaskDependenciesStoreV2(state => state.isBlocked);

export const useBlockingReasons = () => 
  useTaskDependenciesStoreV2(state => state.blockingReasons);

export const useTaskDependencyInfo = (taskId: string) => 
  useTaskDependenciesStoreV2(state => state.taskInfoMap.get(taskId));
